import { useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import {
  type IndexingStatusResponse,
  type LibraryStatus,
  purgeIndex,
  triggerIndexing,
} from "@/api/admin-api"
import { AlertCircle, Loader2, Play, Trash2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

type IndexingManagementCardProps = {
  libraries: LibraryStatus[]
  status: IndexingStatusResponse | null
  onCheckStatus: () => Promise<void>
  onRefresh?: () => void
}

export function IndexingControlPanel({
  libraries,
  status,
  onCheckStatus,
  onRefresh,
}: IndexingManagementCardProps) {
  const { t } = useTranslation()
  const [isRunning, setIsRunning] = useState(false)
  const [isPurging, setIsPurging] = useState(false)
  const [selectedLibrary, setSelectedLibrary] = useState<string>("all")
  const [purgeDialogOpen, setPurgeDialogOpen] = useState(false)
  const [purgeLibrary, setPurgeLibrary] = useState<string>("")

  const handleRunIndexing = async () => {
    setIsRunning(true)
    try {
      const indexId = selectedLibrary === "all" ? undefined : selectedLibrary
      const result = await triggerIndexing(indexId)

      toast.success(t("adminDashboard.indexing.triggered", "Indexing triggered"), {
        description: result.message,
      })

      // Refresh status after triggering
      await onCheckStatus()
      onRefresh?.()
    } catch (error) {
      toast.error(t("adminDashboard.indexing.triggerError", "Failed to trigger indexing"), {
        description: error instanceof Error ? error.message : String(error),
      })
    } finally {
      setIsRunning(false)
    }
  }

  const handlePurge = async () => {
    if (!purgeLibrary) return

    setIsPurging(true)
    try {
      const result = await purgeIndex(purgeLibrary)

      toast.success(t("adminDashboard.indexing.purged", "Index purged"), {
        description: result.message || `Index ${purgeLibrary} has been purged`,
      })

      setPurgeDialogOpen(false)
      setPurgeLibrary("")
      onRefresh?.()
    } catch (error) {
      toast.error(t("adminDashboard.indexing.purgeError", "Failed to purge index"), {
        description: error instanceof Error ? error.message : String(error),
      })
    } finally {
      setIsPurging(false)
    }
  }

  const enabledLibraries = libraries.filter(
    (lib) => lib.indexing_enabled !== false && lib.enabled !== false
  )

  return (
    <>
      <div className="space-y-6">
        {/* Status Display - only show if not available */}
        {status && status.status !== "available" && (
          <div className="border-destructive/50 bg-destructive/10 rounded-lg border p-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="text-destructive size-4" />
              <span className="text-destructive text-sm font-medium">
                {status.status === "not_configured"
                  ? t(
                      "adminDashboard.indexing.statusNotConfigured",
                      "Indexing Service Not Configured"
                    )
                  : t("adminDashboard.indexing.statusError", "Indexing Service Error")}
              </span>
            </div>
            <p className="text-muted-foreground mt-1 text-xs">{status.message}</p>
          </div>
        )}

        {/* Run Indexing Section */}
        <div className="space-y-4">
          <h4 className="font-medium">{t("adminDashboard.indexing.runTitle", "Run Indexing")}</h4>
          <div className="flex flex-wrap items-center gap-4">
            <Select value={selectedLibrary} onValueChange={setSelectedLibrary}>
              <SelectTrigger className="w-[280px]">
                <SelectValue
                  placeholder={t("adminDashboard.indexing.selectLibrary", "Select library")}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("adminDashboard.indexing.allLibraries", "All Libraries")}
                </SelectItem>
                {enabledLibraries.map((lib) => (
                  <SelectItem key={lib.id} value={lib.id}>
                    {lib.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              disabled={isRunning || !status || status.status !== "available"}
              type="button"
              onClick={handleRunIndexing}
            >
              {isRunning ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <Play className="mr-2 size-4" />
              )}
              {t("adminDashboard.indexing.runButton", "Run Indexing")}
            </Button>
          </div>
          <p className="text-muted-foreground text-sm">
            {selectedLibrary === "all"
              ? t(
                  "adminDashboard.indexing.runAllDescription",
                  "This will process all enabled libraries and update their search indexes."
                )
              : t(
                  "adminDashboard.indexing.runSelectedDescription",
                  "This will process only the selected library and update its search index."
                )}
          </p>
        </div>

        {/* Purge Section */}
        <div className="space-y-4">
          <h4 className="font-medium">{t("adminDashboard.indexing.purgeTitle", "Purge Index")}</h4>
          <div className="flex flex-wrap items-center gap-4">
            <Select value={purgeLibrary} onValueChange={setPurgeLibrary}>
              <SelectTrigger className="w-[280px]">
                <SelectValue
                  placeholder={t(
                    "adminDashboard.indexing.selectLibraryToPurge",
                    "Select library to purge"
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                {libraries.map((lib) => (
                  <SelectItem key={lib.id} value={lib.id}>
                    {lib.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              disabled={!purgeLibrary || !status || status.status !== "available"}
              type="button"
              variant="destructive"
              onClick={() => setPurgeDialogOpen(true)}
            >
              <Trash2 className="mr-2 size-4" />
              {t("adminDashboard.indexing.purgeButton", "Purge Index")}
            </Button>
          </div>
          <p className="text-muted-foreground text-sm">
            {t(
              "adminDashboard.indexing.purgeDescription",
              "Warning: This will remove all documents from the selected library's search index. The documents will be re-indexed on the next indexing run."
            )}
          </p>
        </div>
      </div>

      {/* Purge Confirmation Dialog */}
      <Dialog open={purgeDialogOpen} onOpenChange={setPurgeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("adminDashboard.indexing.purgeConfirmTitle", "Confirm Index Purge")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "adminDashboard.indexing.purgeConfirmDescription",
                "Are you sure you want to purge the index for this library? All documents will be removed from the search index. This action cannot be undone."
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-destructive/10 rounded-lg border p-4">
            <p className="text-destructive font-medium">
              {libraries.find((l) => l.id === purgeLibrary)?.display_name || purgeLibrary}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPurgeDialogOpen(false)}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button disabled={isPurging} variant="destructive" onClick={handlePurge}>
              {isPurging && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t("adminDashboard.indexing.confirmPurge", "Purge Index")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
